import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { Delete, Visibility, CheckCircle, BusinessCenter, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from './config';
import { AdminPageShell, AdminStat, AdminSurface } from './admin/AdminPageShell';

const formatWarningLines = (warnings = []) => warnings.filter(Boolean);

const buildDeletionAlert = ({ responseMessage, fallbackMessage, warnings = [] }) => {
    const warningLines = formatWarningLines(warnings);

    return {
        severity: warningLines.length > 0 ? 'warning' : 'success',
        text: responseMessage || [fallbackMessage, ...warningLines].filter(Boolean).join(' '),
    };
};

const BusinessList = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [businesses, setBusinesses] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('');
    const [actionMessage, setActionMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(API_URL + 'get_businesses.php', {
                    params: { page: page + 1, limit: rowsPerPage },
                });
                setBusinesses(response.data.data || []);
            } catch (err) {
                setError('Failed to fetch businesses');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, rowsPerPage]);

    const approvedCount = useMemo(
        () => businesses.filter((business) => business.approved).length,
        [businesses],
    );

    const handleView = (id) => navigate(`/business/${id}`);

    const closeDialog = useCallback(() => {
        setOpenDialog(false);
        setSelectedBusiness(null);
        setDialogType('');
    }, []);

    const handleDelete = async () => {
        if (!selectedBusiness) {
            return;
        }

        const businessToDelete = selectedBusiness;

        try {
            setSubmitting(true);
            setActionMessage(null);

            const response = await axios.post(
                API_URL + 'delete_business.php',
                { data: { id: businessToDelete.id } },
                { withCredentials: true },
            );

            setBusinesses((prev) => prev.filter((business) => business.id !== businessToDelete.id));
            closeDialog();
            setActionMessage(buildDeletionAlert({
                responseMessage: response.data?.message,
                fallbackMessage: `${businessToDelete.name} deleted successfully.`,
                warnings: response.data?.warnings,
            }));
        } catch (err) {
            closeDialog();
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to delete business.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = async () => {
        try {
            setSubmitting(true);
            setActionMessage(null);
            await axios.put('/api/business-approve.php', { id: selectedBusiness.id });
            setBusinesses((prev) =>
                prev.map((business) =>
                    business.id === selectedBusiness.id ? { ...business, approved: true } : business,
                ),
            );
            setActionMessage({
                severity: 'success',
                text: `${selectedBusiness.name} approved successfully.`,
            });
            closeDialog();
        } catch (err) {
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to approve business.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangePage = (_event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <AdminPageShell
                icon={<BusinessCenter />}
                eyebrow="Business directory"
                title="Manage Businesses"
                description="Loading the current business directory."
                maxWidth="xl"
            >
                <AdminSurface sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </AdminSurface>
            </AdminPageShell>
        );
    }

    if (error) {
        return (
            <AdminPageShell
                icon={<BusinessCenter />}
                eyebrow="Business directory"
                title="Manage Businesses"
                description="There was a problem loading the business directory."
                maxWidth="xl"
            >
                <Alert severity="error">{error}</Alert>
            </AdminPageShell>
        );
    }

    return (
        <AdminPageShell
            icon={<BusinessCenter />}
            eyebrow="Business directory"
            title="Manage Businesses"
            description="Browse, review, approve, or remove businesses from a cleaner dashboard-style list that matches the other admin pages."
            maxWidth="xl"
        >
            <Stack spacing={3}>
                {actionMessage && (
                    <Alert severity={actionMessage.severity} onClose={() => setActionMessage(null)}>
                        {actionMessage.text}
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <AdminStat label="Businesses on page" value={businesses.length} helper="Current dataset loaded for this page." />
                    <AdminStat label="Approved" value={approvedCount} helper="Businesses already approved in the current list." />
                    <AdminStat label="Pending" value={businesses.length - approvedCount} helper="Businesses still waiting for approval." />
                </Stack>

                <AdminSurface sx={{ p: 0, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            px: { xs: 2, md: 3 },
                            py: 2.5,
                            borderBottom: '1px solid rgba(106, 90, 249, 0.1)',
                            background: 'linear-gradient(180deg, rgba(248,249,255,0.95) 0%, rgba(255,255,255,0.95) 100%)',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                            Business records
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            Use the actions column to view details, delete a business, or approve it when needed.
                        </Typography>
                    </Box>

                    {isSmallScreen ? (
                        <Stack spacing={1.5} sx={{ p: 2 }}>
                            {businesses.map((business) => (
                                <AdminSurface key={business.id} sx={{ p: 2 }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                                    {business.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    ID: {business.id}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={business.approved ? 'Approved' : 'Pending'}
                                                color={business.approved ? 'success' : 'warning'}
                                                variant={business.approved ? 'filled' : 'outlined'}
                                                sx={{ fontWeight: 700, flexShrink: 0 }}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <Button size="small" variant="outlined" onClick={() => handleView(business.id)}>View</Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                onClick={() => {
                                                    setSelectedBusiness(business);
                                                    setDialogType('delete');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                            {!business.approved && (
                                                <Button
                                                    size="small"
                                                    color="success"
                                                    variant="contained"
                                                    onClick={() => {
                                                        setSelectedBusiness(business);
                                                        setDialogType('approve');
                                                        setOpenDialog(true);
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </AdminSurface>
                            ))}
                        </Stack>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table aria-label="business table" sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Business</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {businesses.map((business) => (
                                        <TableRow key={business.id} hover>
                                            <TableCell sx={{ py: 2 }}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                                        {business.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ID: {business.id}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ py: 2 }}>
                                                <Chip
                                                    label={business.approved ? 'Approved' : 'Pending'}
                                                    color={business.approved ? 'success' : 'warning'}
                                                    variant={business.approved ? 'filled' : 'outlined'}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 2 }}>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    <IconButton color="info" onClick={() => handleView(business.id)}>
                                                        <Visibility />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedBusiness(business);
                                                            setDialogType('delete');
                                                            setOpenDialog(true);
                                                        }}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                    {!business.approved && (
                                                        <IconButton
                                                            color="success"
                                                            onClick={() => {
                                                                setSelectedBusiness(business);
                                                                setDialogType('approve');
                                                                setOpenDialog(true);
                                                            }}
                                                        >
                                                            <CheckCircle />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={businesses.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Rows per page"
                        sx={{
                            borderTop: '1px solid rgba(106, 90, 249, 0.1)',
                            '& .MuiTablePagination-toolbar': {
                                flexWrap: 'wrap',
                                gap: 1,
                                px: { xs: 2, md: 3 },
                            },
                        }}
                    />
                </AdminSurface>
            </Stack>

            <Dialog open={openDialog} onClose={submitting ? undefined : closeDialog} fullWidth maxWidth="xs">
                <DialogTitle>{dialogType === 'delete' ? 'Confirm Delete' : 'Confirm Approval'}</DialogTitle>
                <DialogContent>
                    {dialogType === 'delete' ? (
                        <Stack spacing={2}>
                            <Alert
                                severity="warning"
                                icon={<WarningAmber fontSize="inherit" />}
                                sx={{ alignItems: 'flex-start' }}
                            >
                                Deleting a business permanently removes its users, records, payment history, and any linked record files. Missing files may still allow the delete to finish with warnings.
                            </Alert>
                            <Typography variant="body2" color="text.secondary">
                                Are you sure you want to delete <strong>{selectedBusiness?.name}</strong>? This action cannot be undone.
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Approve {selectedBusiness?.name}?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={dialogType === 'delete' ? handleDelete : handleApprove}
                        color={dialogType === 'delete' ? 'error' : 'success'}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? 'Working...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageShell>
    );
};

export default BusinessList;
